import { TransformedData } from '@aibees/elephant/components/export-dialog';
import { useSanitizeExportSetting } from '@aibees/elephant/components/export-dialog/hooks/use-sanitize-export-setting';
import { EnrichmentTypeEnum } from '@aibees/elephant/types';
import { useBrowsePresetApi } from '@aibees/shared/api';
import { TemplateCategory, TransferredData } from '@aibees/shared/features';
import { useMemo } from 'react';
import { useEnrichmentTemplateDefaultItem } from './use-enrichment-template-default-item';
import { filter, lowerCase, map } from 'lodash-es';

interface UseConvertEnrichmentDataToExportSettingProps {
  selectedTemplateName: string | undefined;
  enrichmentType?: EnrichmentTypeEnum;
}

export function useConvertEnrichmentDataToExportSetting({
  selectedTemplateName,
  enrichmentType,
}: UseConvertEnrichmentDataToExportSettingProps): TransformedData | undefined {
  const defaultItems = useEnrichmentTemplateDefaultItem(enrichmentType);
  const sanitizeExportSetting = useSanitizeExportSetting();

  const emailTemplateType =
    enrichmentType === EnrichmentTypeEnum.EMAIL ||
    enrichmentType === EnrichmentTypeEnum.EMAIL_FINDER ||
    enrichmentType === EnrichmentTypeEnum.EMAIL_VERIFIER
      ? 'email'
      : undefined;

  const type = emailTemplateType ? EnrichmentTypeEnum.EMAIL : enrichmentType;

  const { data } = useBrowsePresetApi<TemplateCategory[]>({
    name: selectedTemplateName,
    type: type as string,
  });

  const handleTransfer = (oldState: TemplateCategory[]): TransferredData[] => {
    return map(oldState, (category) => {
      const checkedEntries = filter(category.entries, 'checked');
      const bottomEntries = filter(category.entries, { isBottom: true });

      // Check if any bottom entries have selected fields
      const bottomEntriesWithSelectedFields = bottomEntries.filter(
        (bottomEntry) => bottomEntry.fields.some((field) => field.selected)
      );

      // Include both checked entries and bottom entries
      const entriesToInclude = [
        ...checkedEntries,
        ...bottomEntries.filter(
          (bottomEntry) =>
            !checkedEntries.some(
              (checkedEntry) => checkedEntry.name === bottomEntry.name
            )
        ),
      ];

      return {
        key: category.key,
        categoryName: lowerCase(category.name),
        disabled: category.disabled,
        isSingleEntry: category.isSingleEntry,
        entries: map(entriesToInclude, (entry, index) => ({
          index: entry.isBottom ? 1000000 : index,
          isBottom: entry.isBottom,
          notAdded:
            entry.isBottom && bottomEntriesWithSelectedFields.length === 0,
          fields: map(filter(entry.fields, 'selected'), (field) => field.type),
        })),
      };
    });
  };

  const transferredData = useMemo(() => {
    return handleTransfer(defaultItems.items);
  }, [defaultItems.items]);

  const items: TemplateCategory[] = useMemo(() => {
    if (selectedTemplateName) {
      const selected = data?.content.find(
        (x) => x.name === selectedTemplateName
      );

      if ((selected?.value as any)?.default) {
        return (data?.content[0]?.value as any)?.items;
      } else {
        return selected?.value;
      }
    } else {
      return transferredData;
    }
  }, [data?.content, selectedTemplateName]);
  const prepareDataForServer = useMemo(() => {
    return items?.reduce<TransformedData>((acc, item) => {
      if (item.isSingleEntry && item.entries[0]) {
        acc[item.key] = { fields: item.entries[0].fields };
      } else if (!item.isSingleEntry) {
        acc[item.name] = item.entries.map((entry, index) => ({
          index: entry.index ?? index,
          fields: entry.fields,
        }));
      }
      return acc;
    }, {});
  }, [items]);

  return useMemo(
    () =>
      prepareDataForServer && Object.keys(prepareDataForServer).length > 0
        ? sanitizeExportSetting(prepareDataForServer)
        : undefined,
    [prepareDataForServer, sanitizeExportSetting]
  );
}
